import React, { useMemo } from 'react';

interface StarData {
  top: string;
  left: string;
  width: string;
  height: string;
  animationDuration: string;
  animationDelay: string;
  opacity?: number;
  background?: string;
}

interface StarProps {
  data: StarData;
  type: 'small' | 'medium' | 'large' | 'constellation';
}

const Star: React.FC<StarProps> = ({ data, type }) => {
  const className = type === 'small' ? 'star-small' : `star-${type}`;
  
  return (
    <div
      className={className}
      style={{
        top: data.top,
        left: data.left,
        width: data.width,
        height: data.height,
        animation: `${type === 'small' ? 'starTwinkle' : 
                     type === 'medium' ? 'starPulse' : 
                     type === 'large' ? 'starGlow' : 'starBreath'} ${data.animationDuration} ${type === 'medium' ? 'ease-in-out infinite alternate' : 'ease-in-out infinite'}`,
        animationDelay: data.animationDelay,
        opacity: data.opacity,
        background: data.background,
      }}
    />
  );
};

export default function Starfield() {
  // Generate star data once and memoize it
  const stars = useMemo(() => {
    const random = (min: number, max: number) => Math.random() * (max - min) + min;
    
    const smallStars: StarData[] = Array.from({ length: 30 }, () => ({
      top: `${random(0, 100)}%`,
      left: `${random(0, 100)}%`,
      width: `${random(0.5, 2.5)}px`,
      height: `${random(0.5, 2.5)}px`,
      animationDuration: `${random(3, 8)}s`,
      animationDelay: `${random(0, 6)}s`,
      opacity: random(0.2, 0.9),
    }));
    
    const mediumStars: StarData[] = Array.from({ length: 12 }, () => {
      const size = random(1.5, 4);
      return {
        top: `${random(0, 100)}%`,
        left: `${random(0, 100)}%`,
        width: `${size}px`,
        height: `${size}px`,
        animationDuration: `${random(4, 11)}s`,
        animationDelay: `${random(0, 7)}s`,
        background: `radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(147,197,253,0.9) 50%, transparent 100%)`,
      };
    });
    
    const largeStars: StarData[] = Array.from({ length: 6 }, () => {
      const size = random(2.5, 5.5);
      return {
        top: `${random(0, 100)}%`,
        left: `${random(0, 100)}%`,
        width: `${size}px`,
        height: `${size}px`,
        animationDuration: `${random(5, 14)}s`,
        animationDelay: `${random(0, 9)}s`,
        background: `radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(168,139,250,1) 40%, rgba(56,189,248,0.8) 70%, transparent 100%)`,
      };
    });
    
    const constellationStars: StarData[] = Array.from({ length: 3 }, () => {
      const size = random(3.5, 7.5);
      return {
        top: `${random(0, 100)}%`,
        left: `${random(0, 100)}%`,
        width: `${size}px`,
        height: `${size}px`,
        animationDuration: `${random(10, 24)}s`,
        animationDelay: `${random(0, 12)}s`,
        background: `radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(34,197,94,0.8) 30%, rgba(16,185,129,0.6) 60%, transparent 100%)`,
      };
    });
    
    return { smallStars, mediumStars, largeStars, constellationStars };
  }, []); // Empty dependency array ensures this only runs once
  
  return (
    <div className="starfield-bg fixed inset-0 -z-[2]">
      
      {/* Small Twinkling Stars */}
      {stars.smallStars.map((data, i) => (
        <Star key={`small-${i}`} data={data} type="small" />
      ))}

      {/* Medium Stars with Glow */}
      {stars.mediumStars.map((data, i) => (
        <Star key={`medium-${i}`} data={data} type="medium" />
      ))}

      {/* Large Bright Stars */}
      {stars.largeStars.map((data, i) => (
        <Star key={`large-${i}`} data={data} type="large" />
      ))}

      {/* Constellation Stars */}
      {stars.constellationStars.map((data, i) => (
        <Star key={`constellation-${i}`} data={data} type="constellation" />
      ))}
    </div>
  );
} 